/* Права админки на стороне интерфейса.

   Каталог прав живёт на сервере (`auth::AdminPermission`, `GET /v1/admin/permissions`): 62 права
   «раздел × действие». Здесь — только коды, которые интерфейс проверяет сам, чтобы спрятать
   раздел меню, страницу или кнопку, и таблица «страница → права», одна на меню, ворота
   страниц и превью «что увидит сотрудник» в редакторе ролей.

   Это удобство, а не граница: сервер проверяет каждое действие сам (`RequirePermission<P>`),
   и спрятанная здесь кнопка без права всё равно получила бы 403. */

export const PERM = {
  fullAccess: 'full_access',
  dashboardView: 'dashboard.view',
  ordersView: 'orders.view',
  ordersViewPricing: 'orders.view_pricing',
  ordersAssign: 'orders.assign',
  ordersCancel: 'orders.cancel',
  ordersIntervene: 'orders.intervene',
  ordersApprove: 'orders.approve',
  ordersNoShow: 'orders.no_show',
  ordersRefund: 'orders.refund',
  ordersReceipts: 'orders.receipts',
  ordersVideos: 'orders.videos',
  ordersDeliveries: 'orders.deliveries',
  operatorsView: 'operators.view',
  operatorsEdit: 'operators.edit',
  operatorsDeactivate: 'operators.deactivate',
  operatorsLock: 'operators.lock',
  operatorsDevice: 'operators.device',
  operatorsWipeDevice: 'operators.wipe_device',
  refillRequestsView: 'refill_requests.view',
  refillRequestsDecide: 'refill_requests.decide',
  chatsOperatorsView: 'chats.operators.view',
  chatsOperatorsSend: 'chats.operators.send',
  chatsConsumersView: 'chats.consumers.view',
  chatsConsumersSend: 'chats.consumers.send',
  consumersView: 'consumers.view',
  consumersBlock: 'consumers.block',
  consumersDiscount: 'consumers.discount',
  clientsView: 'clients.view',
  clientsEdit: 'clients.edit',
  clientsDeactivate: 'clients.deactivate',
  clientsReports: 'clients.reports',
  providersView: 'providers.view',
  providersEdit: 'providers.edit',
  providersDelete: 'providers.delete',
  providersRotateToken: 'providers.rotate_token',
  catalogFuelTypesView: 'catalog.fuel_types.view',
  catalogFuelTypesEdit: 'catalog.fuel_types.edit',
  catalogFuelStationsView: 'catalog.fuel_stations.view',
  catalogFuelStationsEdit: 'catalog.fuel_stations.edit',
  catalogDepartmentsView: 'catalog.departments.view',
  catalogDepartmentsEdit: 'catalog.departments.edit',
  catalogRefuelCarsView: 'catalog.refuel_cars.view',
  catalogRefuelCarsEdit: 'catalog.refuel_cars.edit',
  catalogRegionsView: 'catalog.regions.view',
  catalogRegionsEdit: 'catalog.regions.edit',
  catalogZonesView: 'catalog.zones.view',
  catalogZonesEdit: 'catalog.zones.edit',
  catalogPersonalCarView: 'catalog.personal_car.view',
  catalogPersonalCarEdit: 'catalog.personal_car.edit',
  pricingView: 'pricing.view',
  pricingEdit: 'pricing.edit',
  reportsView: 'reports.view',
  reportsCustom: 'reports.custom',
  reportsAuditFields: 'reports.audit_fields',
  appReleasesManage: 'app_releases.manage',
  accountsUnlock: 'accounts.unlock',
  adminsView: 'admins.view',
  adminsEdit: 'admins.edit',
  adminsDeactivate: 'admins.deactivate',
  rolesView: 'roles.view',
  rolesEdit: 'roles.edit',
} as const;

export type PermissionCode = (typeof PERM)[keyof typeof PERM];

/** Вкладки «Справочников» и право, которое открывает каждую. */
export const CATALOG_TAB_ACCESS = {
  'fuel-types': PERM.catalogFuelTypesView,
  departments: PERM.catalogDepartmentsView,
  'fuel-stations': PERM.catalogFuelStationsView,
  'refuel-cars': PERM.catalogRefuelCarsView,
  equipment: PERM.catalogRefuelCarsView,
  'admin-levels': PERM.catalogRegionsView,
} as const;

/** Страница → права, любого из которых достаточно, чтобы её открыть, и её название в меню.
    Порядок — порядок меню: по нему же выбирается страница, куда вести сотрудника без доступа к
    главной. */
export const ROUTE_ACCESS: ReadonlyArray<{
  to: string;
  i18nKey: string;
  anyOf: readonly string[];
}> = [
  { to: '/', i18nKey: 'nav.dashboard', anyOf: [PERM.dashboardView, PERM.ordersView] },
  { to: '/map', i18nKey: 'nav.map', anyOf: [PERM.ordersView] },
  { to: '/orders', i18nKey: 'nav.orders', anyOf: [PERM.ordersView] },
  { to: '/operators', i18nKey: 'nav.operators', anyOf: [PERM.operatorsView] },
  { to: '/providers', i18nKey: 'nav.providers', anyOf: [PERM.providersView] },
  { to: '/clients', i18nKey: 'nav.clients', anyOf: [PERM.clientsView] },
  {
    to: '/catalog',
    i18nKey: 'nav.catalog',
    anyOf: [...new Set(Object.values(CATALOG_TAB_ACCESS))],
  },
  { to: '/consumers', i18nKey: 'nav.consumers', anyOf: [PERM.consumersView] },
  { to: '/consumer-zones', i18nKey: 'nav.consumer_zones', anyOf: [PERM.catalogZonesView] },
  { to: '/pricing', i18nKey: 'nav.pricing', anyOf: [PERM.pricingView] },
  {
    to: '/personal-car-settings',
    i18nKey: 'nav.personal_car_settings',
    anyOf: [PERM.catalogPersonalCarView],
  },
  { to: '/stuck-deliveries', i18nKey: 'nav.stuck_deliveries', anyOf: [PERM.ordersDeliveries] },
  { to: '/refill-requests', i18nKey: 'nav.refill_requests', anyOf: [PERM.refillRequestsView] },
  {
    to: '/chat',
    i18nKey: 'nav.chat',
    anyOf: [PERM.chatsOperatorsView, PERM.chatsConsumersView],
  },
  { to: '/reports', i18nKey: 'nav.reports', anyOf: [PERM.reportsView, PERM.reportsCustom] },
  { to: '/reports/builder', i18nKey: 'nav.report_builder', anyOf: [PERM.reportsCustom] },
  { to: '/admins', i18nKey: 'nav.admins', anyOf: [PERM.adminsView] },
  { to: '/admin-roles', i18nKey: 'nav.admin_roles', anyOf: [PERM.rolesView] },
];

function routeEntry(to: string) {
  const entry = ROUTE_ACCESS.find((r) => r.to === to);
  if (!entry) throw new Error(`no access rule for route ${to}`);
  return entry;
}

/** Права страницы по её адресу (для ворот в роутере и меню). */
export function routeAccess(to: string): readonly string[] {
  return routeEntry(to).anyOf;
}

/** Ключ перевода с названием страницы в меню. */
export function routeLabelKey(to: string): string {
  return routeEntry(to).i18nKey;
}

/** Решение guard'а сервера: полный доступ пропускает всё, иначе — точное совпадение. */
export function hasPermissionIn(
  codes: ReadonlyArray<string> | null | undefined,
  code: string,
): boolean {
  if (!codes) return false;
  return codes.includes(PERM.fullAccess) || codes.includes(code);
}

/** Любое из прав. */
export function hasAnyPermissionIn(
  codes: ReadonlyArray<string> | null | undefined,
  anyOf: ReadonlyArray<string>,
): boolean {
  return anyOf.some((code) => hasPermissionIn(codes, code));
}

/** Страницы, которые откроются с этим набором прав, в порядке меню. */
export function reachableRoutes(codes: ReadonlyArray<string> | null | undefined): string[] {
  return ROUTE_ACCESS.filter((r) => hasAnyPermissionIn(codes, r.anyOf)).map((r) => r.to);
}
